import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Form, Modal } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import PhotoUploadModal from "./PhotoUploadModal";
import moment from "moment";

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#0dcaf0", color: "#000" },
    IN_PROGRESS: { backgroundColor: "#ffc107", color: "#000" },
    COMPLETED: { backgroundColor: "#198754", color: "#fff" },
    SKIPPED: { backgroundColor: "#6c757d", color: "#fff" },
    OVERDUE: { backgroundColor: "#ff5454", color: "#fff" },
  };
  return styles[status] || styles.PENDING;
};

const getTaskTypeStyle = (type) => {
  const styles = {
    CLEANING: { backgroundColor: "#0dcaf0", color: "#000" },
    INSPECTION: { backgroundColor: "#0d6efd", color: "#fff" },
    MAINTENANCE: { backgroundColor: "#ffc107", color: "#000" },
    SANITIZATION: { backgroundColor: "#198754", color: "#fff" },
    SAFETY_CHECK: { backgroundColor: "#ff5454", color: "#fff" },
    OTHER: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[type] || styles.OTHER;
};

const ProviderTaskDetail = ({
  task,
  loading,
  error,
  startingTask,
  completingTask,
  uploadingPhoto,
  actionError,
  onStartTask,
  onCompleteTask,
  onUploadPhoto,
  onRefresh,
}) => {
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [completionNotes, setCompletionNotes] = useState("");

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="provider-task-detail">
        <div className="alert alert-danger">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading task"}
        </div>
        <Link to="/provider-portal/tasks" className="btn btn-secondary">
          <i className="fa fa-arrow-left me-1"></i> Back to Tasks
        </Link>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="provider-task-detail">
        <div className="alert alert-warning">
          <i className="fa fa-exclamation-triangle me-2"></i>
          Task not found
        </div>
        <Link to="/provider-portal/tasks" className="btn btn-secondary">
          <i className="fa fa-arrow-left me-1"></i> Back to Tasks
        </Link>
      </div>
    );
  }

  const isOverdue = task.is_overdue || task.status === "OVERDUE";
  const canStart = task.status === "PENDING" || (isOverdue && task.status !== "COMPLETED" && task.status !== "IN_PROGRESS");
  const canComplete = task.status === "IN_PROGRESS";
  const needsPhoto = task.requires_photo && (!task.photos || task.photos.length === 0);
  const isCompleted = task.status === "COMPLETED" || task.status === "SKIPPED";

  const handleStartTask = async () => {
    try {
      await onStartTask(task.id);
      // Refresh task after starting
      onRefresh();
    } catch (err) {
      console.error("Error starting task:", err);
    }
  };

  const handleCompleteTask = async () => {
    if (needsPhoto) {
      return;
    }
    try {
      await onCompleteTask(task.id, { completion_notes: completionNotes });
      setShowCompleteModal(false);
      setCompletionNotes("");
      onRefresh();
    } catch (err) {
      console.error("Error completing task:", err);
    }
  };

  const handlePhotoUpload = async (formData) => {
    try {
      await onUploadPhoto(task.id, formData);
      setShowPhotoModal(false);
      onRefresh();
    } catch (err) {
      console.error("Error uploading photo:", err);
    }
  };

  return (
    <div className="provider-task-detail">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link to="/provider-portal/tasks" className="btn btn-secondary btn-sm">
          <i className="fa fa-arrow-left me-1"></i> Back to Tasks
        </Link>
        <button className="btn btn-secondary btn-sm" onClick={onRefresh}>
          <i className="fa fa-refresh me-1"></i> Refresh
        </button>
      </div>

      {actionError && (
        <div className="alert alert-danger mb-3">
          <i className="fa fa-exclamation-circle me-2"></i>
          {actionError.message || "An error occurred"}
        </div>
      )}

      {/* Task Header */}
      <Card className={`mb-3 ${isOverdue ? "border-danger" : ""}`}>
        <Card.Header className="card-header-grey d-flex justify-content-between align-items-center">
          <div>
            <strong>{task.task_name}</strong>
            {task.requires_photo && (
              <i className="fa fa-camera ms-2 text-muted" title="Photo required"></i>
            )}
          </div>
          <div className="d-flex gap-2">
            <span className="badge badge-pill" style={getTaskTypeStyle(task.task_type)}>
              {task.task_type_display || task.task_type}
            </span>
            <span className="badge badge-pill" style={getStatusStyle(isOverdue ? "OVERDUE" : task.status)}>
              {isOverdue ? "Overdue" : (task.status_display || task.status)}
            </span>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className="small text-muted d-block mb-1">Target</label>
                <span>
                  <i className={`fa ${task.target_type === "machine" ? "fa-cog" : "fa-building"} me-1 text-muted`}></i>
                  {task.target_name}
                </span>
              </div>
              <div className="mb-3">
                <label className="small text-muted d-block mb-1">Location</label>
                <span>
                  <i className="fa fa-map-marker me-1 text-muted"></i>
                  {task.location_name}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className="small text-muted d-block mb-1">Scheduled Date</label>
                <span>
                  <i className="fa fa-calendar me-1 text-muted"></i>
                  {moment(task.scheduled_date).format("dddd, MMMM D, YYYY")}
                </span>
              </div>
              {task.due_time && (
                <div className="mb-3">
                  <label className="small text-muted d-block mb-1">Due Time</label>
                  <span>
                    <i className="fa fa-clock-o me-1 text-muted"></i>
                    {task.due_time.substring(0, 5)}
                  </span>
                </div>
              )}
              {task.estimated_duration_minutes > 0 && (
                <div className="mb-3">
                  <label className="small text-muted d-block mb-1">Estimated Duration</label>
                  <span>
                    <i className="fa fa-hourglass-half me-1 text-muted"></i>
                    {task.estimated_duration_minutes} minutes
                  </span>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Instructions */}
      {task.instructions && (
        <Card className="mb-3">
          <Card.Header className="card-header-grey">
            <strong><i className="fa fa-list-ol me-2"></i>Instructions</strong>
          </Card.Header>
          <Card.Body>
            <div style={{ whiteSpace: "pre-wrap" }}>{task.instructions}</div>
          </Card.Body>
        </Card>
      )}

      {/* Photos Section */}
      <Card className="mb-3">
        <Card.Header className="card-header-grey d-flex justify-content-between align-items-center">
          <div>
            <strong><i className="fa fa-camera me-2"></i>Photos</strong>
            {task.requires_photo && (
              <span className="badge badge-pill ms-2" style={{ backgroundColor: needsPhoto ? "#ff5454" : "#198754", color: "#fff" }}>
                {needsPhoto ? "Required" : "Uploaded"}
              </span>
            )}
          </div>
          {!isCompleted && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowPhotoModal(true)}>
              <i className="fa fa-plus me-1"></i> Add Photo
            </button>
          )}
        </Card.Header>
        <Card.Body>
          {task.photos && task.photos.length > 0 ? (
            <Row>
              {task.photos.map((photo, index) => (
                <Col md={4} sm={6} key={photo.id || index} className="mb-3">
                  <Card>
                    <img
                      src={photo.photo || photo.url}
                      alt={photo.caption || `Photo ${index + 1}`}
                      style={{ width: "100%", height: "200px", objectFit: "cover" }}
                    />
                    {photo.caption && (
                      <Card.Body className="p-2">
                        <small className="text-muted">{photo.caption}</small>
                      </Card.Body>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="empty-table-message" style={{ marginBottom: 0 }}>
              <i className="fa fa-image me-2"></i>
              No photos uploaded yet
              {task.requires_photo && !isCompleted && (
                <span className="text-danger ms-2">
                  - Photo required before completing
                </span>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Completion Notes (for completed tasks) */}
      {isCompleted && task.completion_notes && (
        <Card className="mb-3">
          <Card.Header className="card-header-grey">
            <strong><i className="fa fa-sticky-note me-2"></i>Completion Notes</strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-0">{task.completion_notes}</p>
          </Card.Body>
        </Card>
      )}

      {/* Action Buttons */}
      {!isCompleted && (
        <Card>
          <Card.Header className="card-header-grey">
            <strong><i className="fa fa-bolt me-2"></i>Actions</strong>
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap gap-2">
              {canStart && (
                <button
                  className="btn btn-warning"
                  onClick={handleStartTask}
                  disabled={startingTask}
                >
                  {startingTask ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      Starting...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-play me-2"></i>
                      Start Task
                    </>
                  )}
                </button>
              )}

              {canComplete && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowPhotoModal(true)}
                  >
                    <i className="fa fa-camera me-2"></i>
                    Upload Photo
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={() => setShowCompleteModal(true)}
                    disabled={needsPhoto}
                    title={needsPhoto ? "Photo required before completing" : "Mark task as complete"}
                  >
                    <i className="fa fa-check me-2"></i>
                    Complete Task
                  </button>

                  {needsPhoto && (
                    <div className="w-100 mt-2">
                      <small className="text-danger">
                        <i className="fa fa-exclamation-triangle me-1"></i>
                        Please upload a photo before completing this task
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Complete Task Modal */}
      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered dialogClassName="modal-logut" animation={false}>
        <Modal.Header closeButton style={{ backgroundColor: "#E8E8E8", borderBottom: "1px solid #dbdee0" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            <i className="fa fa-check-circle me-2"></i>
            Complete Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            You are about to mark <strong>{task.task_name}</strong> as completed.
          </p>
          <Form.Group>
            <Form.Label className="small">Completion Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="Add any notes about the completed task..."
              disabled={completingTask}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCompleteModal(false)} disabled={completingTask}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={handleCompleteTask}
            disabled={completingTask}
          >
            {completingTask ? (
              <>
                <i className="fa fa-spinner fa-spin me-1"></i>
                Completing...
              </>
            ) : (
              <>
                <i className="fa fa-check me-1"></i>
                Complete Task
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        show={showPhotoModal}
        onHide={() => setShowPhotoModal(false)}
        onUpload={handlePhotoUpload}
        uploading={uploadingPhoto}
        error={actionError}
      />
    </div>
  );
};

export default ProviderTaskDetail;
